import { Spacing } from '@persona-id/design-tokens';
import {
  CircleAlertIcon,
  CircleHelpIcon,
  SidebarLeftIcon,
  SidebarRightIcon,
  XIcon,
} from '@persona-id/design-tokens/icons';
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import {
  InquiryTemplateSimulateStepsNavigation,
  InquiryTemplateSimulateStubbing,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview';
import { getScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/selectors/screenDimensions';
import { InquiryTemplateSimulateFormData } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/types';
import {
  ScreenDimensions,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { TOOLBAR_TRANSITION } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import { clampScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/utils/screen-dimensions';
import LoadingModal from 'dashboard/components/LoadingModal';
import EditorToolbar from 'dashboard/components-lib/EditorToolbar';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import NullState from 'dashboard/components-lib/NullState';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';
import { updatePreviewScreenDimensions } from 'dashboard/redux/thunk-actions/inquiry-preview';
import { Z_INDICES } from 'dashboard/styles/variables';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import { getRootDomain } from 'lib/utils/url';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import { FromWidgetEvent } from 'verify/lib/clients/HolderClient';

export const INQUIRY_PREVIEW_ID = 'inquiry_preview_id';
export const INQUIRY_WIDGET_CONTAINER_ID = 'inquiry_widget_container_id';
export const SIDE_PANEL_CONTAINER_RIGHT_ID = 'side_panel_container_right_id';

const HOSTED_FLOW_DOC_LINK = 'https://docs.withpersona.com/docs/hosted-flow';
const EMBEDDED_FLOW_DOC_LINK = 'https://docs.withpersona.com/docs/embedded-flow';

enum PreviewFlowType {
  Hosted = 'hosted',
  Embedded = 'embedded',
  Inline = 'inline',
}

const SimulateContainer = styled.div`
  display: flex;
  flex-flow: column;
  height: 100vh;
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const BannerContainer = styled.div`
  // 70px is the exact margin provided between the top of Inquiry frame and the top of the widget.
  // Use a fixed height for this container so that the banner content can still be centered
  // as the banner is narrowed and the content overflows to multiple lines
  height: 70px;
  display: flex;
  position: absolute;
  top: 0px;
  width: 100%;
`;

const CircleAlertContainer = styled.div`
  width: 16px;
  height: 16px;
  margin-right: ${Spacing[2]};
`;

const StyledButton = styled(Button)`
  background-color: unset;
`;

const StyledBanner = styled(Banner)`
  ${paragraphMixins.sm};
  position: relative;
  top: 0px;
  align-self: center;
  margin-left: ${Spacing[4]};
  margin-right: ${Spacing[4]};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${Spacing[2]} ${Spacing[5]};
  z-index: 1;
  box-shadow: ${Elevation.Low};
`;

const IframeContainer = styled.div<{ hidden: boolean; disabled: boolean }>`
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const panelStyling = css`
  overflow: hidden;
  height: 100%;
  position: absolute;
  z-index: ${Z_INDICES.withShadow};
  transition: transform ${TOOLBAR_TRANSITION};
`;

const SidePanelContainerLeft = styled.div<{ visible: boolean }>`
  ${panelStyling};
  left: 0;
  transform: ${({ visible }) => `translateX(${visible ? '0px' : '-100%'})`};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SidePanelContainerRight = styled.div<{ visible: boolean }>`
  ${panelStyling};
  right: 0;
  transform: ${({ visible }) => `translateX(${visible ? '0px' : '100%'})`};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const PaddedSimulateContainer = styled.div<{ addLeftPadding: boolean; addRightPadding: boolean }>`
  display: flex;
  position: relative;
  width: 100%;
  // Using margin so the BannerContainer can be 100% width and still be fully
  // contained within the visible area of PaddedSimulateContainer
  ${({ addLeftPadding }) => addLeftPadding && `margin-left: 300px`};
  ${({ addRightPadding }) => addRightPadding && `margin-right: 450px`};
  transition: all 200ms cubic-bezier(0.47, 0, 0.745, 0.715);
  justify-content: center;
`;

const InlineFlowScreenParentContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: #505060;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  overflow: auto;
  // Top padding should be 90px to leave room for simulate banner
  padding: 90px ${Spacing[6]};
  align-items: center;
`;

const InlineFlowScreenContainer = styled.div.attrs<{
  screenDimensions?: ScreenDimensions;
}>(({ screenDimensions }) => ({
  style: {
    width: `${screenDimensions?.width ?? PREVIEW_WIDTH}px`,
    minHeight: `${screenDimensions?.height ?? FRAME_HEIGHT}px`,
  },
}))<{ screenDimensions?: ScreenDimensions }>`
  margin: auto;
`;

export interface SimulateIframeProps {
  id: string;
  counter: number;
  hidden: boolean;
  onLoad: () => void;
  source: string;
  disabled: boolean;
}

const SimulateIframe = ({ id, counter, hidden, onLoad, source, disabled }: SimulateIframeProps) => {
  return (
    <IframeContainer hidden={hidden} disabled={disabled}>
      <iframe
        id={id}
        key={counter}
        onLoad={onLoad}
        style={{ position: 'relative', width: '100%', height: '100%' }}
        allow='camera; microphone'
        sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation'
        src={source}
      ></iframe>
    </IframeContainer>
  );
};

const ScreenParentContainer = ({
  isInlineFlow,
  children,
}: {
  isInlineFlow: boolean;
  children: ReactNode;
}): JSX.Element => {
  if (isInlineFlow) {
    return (
      <InlineFlowScreenParentContainer data-test='inline-flow-parent-container'>
        {children}
      </InlineFlowScreenParentContainer>
    );
  } else {
    return <>{children}</>;
  }
};

const ScreenContainer = ({
  isInlineFlow,
  screenDimensions,
  children,
}: {
  isInlineFlow: boolean;
  children: ReactNode;
  screenDimensions?: ScreenDimensions;
}): JSX.Element => {
  if (isInlineFlow) {
    return (
      <InlineFlowScreenContainer screenDimensions={screenDimensions}>
        {children}
      </InlineFlowScreenContainer>
    );
  } else {
    return <>{children}</>;
  }
};

const generateIframeSrc = (
  host: string,
  inquiryId: string,
  flowType: PreviewFlowType,
  messageTargetOrigin: string,
) => {
  return `${host}/widget?environment=sandbox&inquiry-id=${inquiryId}&iframe-origin=${host}&flow-type=${flowType}&sandbox-tools-disabled=true&sandbox-banner-disabled=true&message-target-origin=${messageTargetOrigin}`;
};

export interface SimulateIFramesProps {
  inquiryId: string;
  iframeCounterOne: number;
  iframeCounterTwo: number;
  hiddenIframeIdx: number;
  disabled: boolean;
  simulateBannerMessage: string | null;
  clearSimulateBannerMessage: () => void;
  flowType: PreviewFlowType;
  addLeftPadding: boolean;
  addRightPadding: boolean;
  setHiddenIframeIdx: (idx: number) => void;
  setCurrentStepName: (stepName: string) => void;
  isSkipTransitioning: boolean;
  setIsSkipTransitioning: (isSkipTransitioning: boolean) => void;
  setNumTransitionPageChanges: Dispatch<SetStateAction<number>>;
  handleLoadInquiry: (inquiryId: string) => Promise<void>;
}

const SimulateIFrames = ({
  inquiryId,
  iframeCounterOne,
  iframeCounterTwo,
  hiddenIframeIdx,
  disabled,
  simulateBannerMessage,
  clearSimulateBannerMessage,
  flowType,
  addLeftPadding,
  addRightPadding,
  setHiddenIframeIdx,
  setCurrentStepName,
  isSkipTransitioning,
  setIsSkipTransitioning,
  setNumTransitionPageChanges,
  handleLoadInquiry,
}: SimulateIFramesProps) => {
  const screenDimensions = useSelector<RootState, ScreenDimensions>((state: RootState) =>
    getScreenDimensionsLiveUpdated(state),
  );

  // Specifies the intended target of the messages emitted by the widget, which is Dashboard.
  // Otherwise, the default target origin would be the origin of the widget, which is not the same as that of
  // Dashboard if the org has a subdomain
  const messageTargetOrigin = location.origin;

  const rootDomain = getRootDomain(location.host);
  const subdomain = rootDomain.includes('localhost') ? null : 'inquiry';

  const host = `${location.protocol}//${[subdomain, rootDomain].filter(Boolean).join('.')}`;
  const iframeSrc = generateIframeSrc(host, inquiryId, flowType, messageTargetOrigin);

  useEffect(() => {
    const handlePageChangeEvent = (e: MessageEvent) => {
      if (e.origin === host) {
        const data = e.data;
        if (data && typeof data === 'object' && data.name === FromWidgetEvent.PageChange) {
          handleLoadInquiry(inquiryId);
          setCurrentStepName(data.metadata.name);
          if (!isSkipTransitioning) {
            setNumTransitionPageChanges((num) => num + 1);
          }
          setIsSkipTransitioning(false);
        }
      }
    };

    window.addEventListener('message', handlePageChangeEvent);

    // On unmount, remove the listener
    return () => {
      window.removeEventListener('message', handlePageChangeEvent);
    };
  }, []);

  return (
    // Having two identical iframes, one of which is hidden, is a way for us to
    // refresh the simulate iframe with less flicker. When we skip to a particular step,
    // we first make the backend request to update the persisted step name. Then, we
    // increment the `key` of the hidden iframe, which causes it to reload (whilst
    // still being hidden). After it finishes loading, the iframe's `onLoad` callback
    // gets called, where we un-hide the updated loaded iframe and hide the stale
    // iframe, thus completing the skipping experience
    <PaddedSimulateContainer addLeftPadding={addLeftPadding} addRightPadding={addRightPadding}>
      <div id={INQUIRY_WIDGET_CONTAINER_ID} />
      <ScreenParentContainer isInlineFlow={flowType === PreviewFlowType.Inline}>
        {simulateBannerMessage != null && (
          <BannerContainer>
            <StyledBanner variant='warning'>
              <CircleAlertContainer>
                <CircleAlertIcon />
              </CircleAlertContainer>
              {simulateBannerMessage}
              <StyledButton variant='inverse' width='icon' onClick={clearSimulateBannerMessage}>
                <XIcon />
              </StyledButton>
            </StyledBanner>
          </BannerContainer>
        )}
        <ScreenContainer
          screenDimensions={screenDimensions}
          isInlineFlow={flowType === PreviewFlowType.Inline}
        >
          <SimulateIframe
            id='iframe0'
            counter={iframeCounterOne}
            hidden={hiddenIframeIdx === 0}
            onLoad={() => setHiddenIframeIdx(1)}
            source={iframeSrc}
            disabled={disabled}
          />
          <SimulateIframe
            id='iframe1'
            counter={iframeCounterTwo}
            hidden={hiddenIframeIdx === 1}
            onLoad={() => setHiddenIframeIdx(0)}
            source={iframeSrc}
            disabled={disabled}
          />
        </ScreenContainer>
      </ScreenParentContainer>
    </PaddedSimulateContainer>
  );
};

interface SimulateWidgetProps {
  inquiryId: string | undefined;
  isLoadingInquiry: boolean;
  iframeCounterOne: number;
  iframeCounterTwo: number;
  hiddenIframeIdx: number;
  simulateBannerMessage: string | null;
  clearSimulateBannerMessage: () => void;
  flowType: PreviewFlowType;
  disabled: boolean;
  addLeftPadding: boolean;
  addRightPadding: boolean;
  setHiddenIframeIdx: (idx: number) => void;
  setCurrentStepName: (stepName: string) => void;
  isSkipTransitioning: boolean;
  setIsSkipTransitioning: (isSkipTransitioning: boolean) => void;
  setNumTransitionPageChanges: Dispatch<SetStateAction<number>>;
  handleLoadInquiry: (inquiryId: string) => Promise<void>;
}

const SimulateWidget = ({
  inquiryId,
  isLoadingInquiry,
  iframeCounterOne,
  iframeCounterTwo,
  hiddenIframeIdx,
  simulateBannerMessage,
  clearSimulateBannerMessage,
  flowType,
  disabled,
  addLeftPadding,
  addRightPadding,
  setHiddenIframeIdx,
  setCurrentStepName,
  isSkipTransitioning,
  setIsSkipTransitioning,
  setNumTransitionPageChanges,
  handleLoadInquiry,
}: SimulateWidgetProps) => {
  if (isLoadingInquiry) {
    // visual loading indicator is handled globally in editor using the LoadingModal
    return <Backdrop />;
  } else if (inquiryId == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t create an inquiry for preview.' />
      </Backdrop>
    );
  }

  return (
    <SimulateIFrames
      inquiryId={inquiryId}
      setCurrentStepName={setCurrentStepName}
      isSkipTransitioning={isSkipTransitioning}
      setIsSkipTransitioning={setIsSkipTransitioning}
      setNumTransitionPageChanges={setNumTransitionPageChanges}
      iframeCounterOne={iframeCounterOne}
      iframeCounterTwo={iframeCounterTwo}
      hiddenIframeIdx={hiddenIframeIdx}
      simulateBannerMessage={simulateBannerMessage}
      clearSimulateBannerMessage={clearSimulateBannerMessage}
      flowType={flowType}
      disabled={disabled}
      addLeftPadding={addLeftPadding}
      addRightPadding={addRightPadding}
      setHiddenIframeIdx={setHiddenIframeIdx}
      handleLoadInquiry={handleLoadInquiry}
    />
  );
};

export interface InquiryPreviewProps {
  inquiry: Inquiry | undefined;
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  header: ReactNode;
  workflowVersionMap: WorkflowVersionMap;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  navConfirmPrompt?: string;
  isLoadingInquiry: boolean;
  isAdvancedMode: boolean;
  toggleAdvancedMode: () => void;
  simulateStubsFormValues: InquiryTemplateSimulateFormData;
  setSimulateStubsFormValues: UseFormSetValue<InquiryTemplateSimulateFormData>;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  handleInitializeSimulateInquiry: () => Promise<void>;
  handleSkipToStep: (stepName: string, inquiryId: string) => Promise<void>;
  handleLoadInquiry: (inquiryId: string) => Promise<void>;
}

const InquiryPreview = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  inquiry,
  header,
  workflowVersionMap,
  isLoadingInquiry,
  verificationTemplateVersions,
  navConfirmPrompt,
  isAdvancedMode,
  toggleAdvancedMode,
  simulateStubsFormValues,
  setSimulateStubsFormValues,
  trackEvent,
  handleInitializeSimulateInquiry,
  handleSkipToStep,
  handleLoadInquiry,
}: InquiryPreviewProps) => {
  const [simulateBannerMessage, setSimulateBannerMessage] = useState<string | null>(null);
  const [flowType, setFlowType] = useState(PreviewFlowType.Hosted);
  const dispatch = useDispatch();

  // This always represents the name of the step that is currently being shown
  const [currentStepName, setCurrentStepName] = useState(
    inquiryTemplateVersion.attributes.steps[0].name,
  );

  const [isStepsListPanelOpen, setIsStepsListPanelOpen] = useLocalStorage(
    LocalStorageKey.DashboardITEPreviewStepsPanelOpen,
    true,
  );

  const [isStubbingPanelOpen, setIsStubbingPanelOpen] = useLocalStorage(
    LocalStorageKey.DashboardITEPreviewSimulatePanelOpen,
    true,
  );

  const [iframeCounterOne, setIframeCounterOne] = useState(0);
  const [iframeCounterTwo, setIframeCounterTwo] = useState(1);
  const [hiddenIframeIdx, setHiddenIframeIdx] = useState(1);
  const [isSkipTransitioning, setIsSkipTransitioning] = useState(false);

  const incrementIframeCounter = useCallback(() => {
    if (hiddenIframeIdx === 0) {
      setIframeCounterOne((value) => value + 2);
    } else {
      setIframeCounterTwo((value) => value + 2);
    }
  }, [hiddenIframeIdx, setIframeCounterOne, setIframeCounterTwo]);

  // Disable the widget so it is non-interactable while the inquiry is being skipped.
  // Then make the request to set the next step name. When that is done, increment the counter
  // so that the iFrame refreshes. Then enable the widget again
  const onSkipToStep = async (stepName: string) => {
    if (inquiry) {
      trackEvent('Flow Editor - Preview Skip Step');
      setIsSkipTransitioning(true);
      await handleSkipToStep(stepName, inquiry.id);
      incrementIframeCounter();
    }
  };

  // Records the number of non-skipping page changes. The child component listens on this value
  // and on-change logs a segment event which certain attributes determinable only in that child
  // component
  const [numTransitionPageChanges, setNumTransitionPageChanges] = useState(0);

  const handleScreenDimensionsChange = useCallback(
    (screenDimensions: ScreenDimensions) => {
      dispatch(updatePreviewScreenDimensions(clampScreenDimensions(screenDimensions)));
    },
    [dispatch, updatePreviewScreenDimensions],
  );

  useEffect(() => {
    // On mount, create the simulated inquiry
    if (inquiry == null) {
      handleInitializeSimulateInquiry();
      setCurrentStepName(inquiryTemplateVersion.attributes.steps[0].name);
    }
  }, [inquiry?.id, inquiryTemplate.id, inquiryTemplateVersion]);

  return (
    <SimulateContainer>
      <LoadingModal title='Loading...' isLoading={isLoadingInquiry || isSkipTransitioning} />
      {navConfirmPrompt && (
        <NavConfirmPrompt prevent={true} message='Are you sure you want to leave?'>
          {navConfirmPrompt}
        </NavConfirmPrompt>
      )}
      {header}
      <EditorToolbar
        id='inquiry-template-preview-toolbar'
        showRightNavContainerBorder={true}
        leftNavItems={
          <ToolbarControlButton
            id='inquiry-template-preview__toggle-navigation'
            tooltipText='Toggle Steps Navigation'
            text='Steps'
            active={isStepsListPanelOpen}
            onClick={() => setIsStepsListPanelOpen((value) => !value)}
            icon={<SidebarLeftIcon />}
          />
        }
        rightNavItems={
          <ToolbarControlButton
            id='inquiry-template-preview__toggle-simulate'
            tooltipText='Toggle Simulate'
            text='Simulate'
            active={isStubbingPanelOpen}
            // toggle the value
            onClick={() => setIsStubbingPanelOpen((value) => !value)}
            icon={<SidebarRightIcon />}
          />
        }
        screenDimensionsInput={{
          inSimulate: true,
          handleScreenDimensionsChange,
          disabled: flowType !== PreviewFlowType.Inline,
        }}
        extraControlsLeft={
          <Tooltip
            trigger={
              <div style={{ alignItems: 'center', display: 'flex' }}>
                <Select
                  aria-label='Flow Type'
                  // Ensure a min width so that all the options can be rendered without
                  // being truncated
                  minWidth={100}
                  key='select-flow-type'
                  id='inquiry-template-preview__select-flow-type'
                  value={flowType}
                  onValueChange={(value) => setFlowType(value as PreviewFlowType)}
                >
                  <Option value={PreviewFlowType.Hosted}>Hosted</Option>
                  <Option value={PreviewFlowType.Embedded}>Embedded</Option>
                  <Option value={PreviewFlowType.Inline}>Inline</Option>
                </Select>
              </div>
            }
            delayDuration={500}
            interactive
            aria-label='Select to switch between hosted, embedded and inline flows'
            zIndex={Z_INDICES.topNavTooltip}
            side='top'
            key='select-flow-type-tooltip'
          >
            <Paragraph color='primary' size='sm' style={{ textAlign: 'center' }}>
              Switch between{' '}
              <a target='_blank' rel='noreferrer' href={HOSTED_FLOW_DOC_LINK}>
                hosted
              </a>{' '}
              ,{' '}
              <a target='_blank' rel='noreferrer' href={EMBEDDED_FLOW_DOC_LINK}>
                embedded
              </a>{' '}
              and inline flows
            </Paragraph>
          </Tooltip>
        }
        extraControlsRight={
          <ToolbarControlButton
            id='inquiry-template-preview-toolbar__help'
            tooltipText='Help article'
            to='https://help.withpersona.com/articles/5NB4eGgy0k8oRs5O5sDOR1'
            icon={<CircleHelpIcon />}
          />
        }
      />
      <ContentContainer>
        <SidePanelContainerLeft visible={isStepsListPanelOpen}>
          <InquiryTemplateSimulateStepsNavigation
            steps={inquiryTemplateVersion.attributes.steps}
            onSelectStep={onSkipToStep}
            currentStepName={currentStepName}
          />
        </SidePanelContainerLeft>
        <SimulateWidget
          inquiryId={inquiry?.id}
          isLoadingInquiry={isLoadingInquiry}
          iframeCounterOne={iframeCounterOne}
          iframeCounterTwo={iframeCounterTwo}
          hiddenIframeIdx={hiddenIframeIdx}
          simulateBannerMessage={simulateBannerMessage}
          clearSimulateBannerMessage={() => setSimulateBannerMessage(null)}
          flowType={flowType}
          disabled={isSkipTransitioning}
          addLeftPadding={isStepsListPanelOpen}
          addRightPadding={isStubbingPanelOpen}
          setHiddenIframeIdx={setHiddenIframeIdx}
          setCurrentStepName={setCurrentStepName}
          isSkipTransitioning={isSkipTransitioning}
          setIsSkipTransitioning={setIsSkipTransitioning}
          setNumTransitionPageChanges={setNumTransitionPageChanges}
          handleLoadInquiry={handleLoadInquiry}
        />
        <SidePanelContainerRight id={SIDE_PANEL_CONTAINER_RIGHT_ID} visible={isStubbingPanelOpen}>
          <InquiryTemplateSimulateStubbing
            inquiry={inquiry}
            verificationTemplateVersions={verificationTemplateVersions}
            inquiryTemplateVersion={inquiryTemplateVersion}
            workflowVersionMap={workflowVersionMap}
            currentStepName={currentStepName}
            numTransitionPageChanges={numTransitionPageChanges}
            setSimulateBannerMessage={setSimulateBannerMessage}
            trackEvent={trackEvent}
            simulateStubsFormValues={simulateStubsFormValues}
            setSimulateStubsFormValues={setSimulateStubsFormValues}
            // config should show loading state until we have created inquiry;
            // otherwise calls to post stubs will fail
            isLoading={inquiry == null}
            isAdvancedMode={isAdvancedMode}
            toggleAdvancedMode={toggleAdvancedMode}
          />
        </SidePanelContainerRight>
      </ContentContainer>
      <div id={INQUIRY_PREVIEW_ID} />
    </SimulateContainer>
  );
};

export default InquiryPreview;
